/**
 * P1/P2 Incident – Ops View
 * P1/P2 Incidents Analysis - Portfolio → Platform → Application
 * Created: March 9, 2026
 */

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { authFetch, getCurrentUser } from '../services/authFetch';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  Checkbox,
  ListItemText,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TablePagination,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  FileDownload,
  Refresh,
  Clear,
  Error as ErrorIcon,
  Close,
  HelpOutline,
  Edit,
} from '@mui/icons-material';
import { INCIDENTS, KPI_TARGETS } from '../config/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface IncidentRecord {
  id: string;
  number: string;
  title: string;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  portfolio: string;
  platform: string;
  application: string;
  createdDate: string;
  resolvedDate?: string;
  status: string;
  mttr?: number; // MTTR in seconds
  mttd?: number; // MTTD in seconds
  outage_duration?: number; // Outage duration in minutes
  hasMttrData?: boolean; // Flag to indicate if MTTR came from API
  hasMttdData?: boolean; // Flag to indicate if MTTD came from API
  oop?: string; // Out-of-policy field; non-empty = OOP incident
}

interface MTTRData {
  mttr: string;
  mttd: string;
  number: string;
  priority: number;
}

interface MetricsByPeriod {
  [period: string]: {
    incidentCount: number;
    totalMTTRSeconds: number;
    lostHours: number;
  };
}

interface HierarchyNode {
  name: string;
  level: 'portfolio' | 'platform' | 'application';
  metrics: MetricsByPeriod;
  records: IncidentRecord[];
  children?: HierarchyNode[];
}

const SELECT_ALL_OPTION = 'Select All';

// ─── Targets by Period Type ────────────────────────────────────────────────
interface PeriodTarget {
  incidentCount: number;
  mttrAvgMinutes: number;
  lostHours: number;
}

// ─── Helper Functions ────────────────────────────────────────────────────────
const formatLocalDate = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getISOWeekYear = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
};

// ─── Dynamic ISO Week to Calendar Month Mapping ──────────────────────────────
/**
 * Calculates which calendar month an ISO week belongs to.
 * An ISO week belongs to the month where Thursday of that week falls.
 * This follows ISO 8601 standard and works for any year.
 */
const getMonthForISOWeek = (year: number, week: number): { year: number; month: number } => {
  // Find the Thursday of this ISO week (ISO weeks are defined by their Thursday)
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const startOfYear = new Date(jan4);
  startOfYear.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

  const thursday = new Date(startOfYear);
  thursday.setUTCDate(startOfYear.getUTCDate() + (week - 1) * 7 + 3); // +3 to get to Thursday

  return {
    year: thursday.getUTCFullYear(),
    month: thursday.getUTCMonth() + 1
  };
};

/**
 * Counts the number of ISO weeks in each month for a given year.
 * This is calculated dynamically based on ISO 8601 rules.
 */
const countWeeksPerMonth = (year: number): { [month: number]: number } => {
  const weeksPerMonth: { [month: number]: number } = {};
  for (let month = 1; month <= 12; month++) {
    weeksPerMonth[month] = 0;
  }

  // Get total weeks in the year
  const dec31 = new Date(Date.UTC(year, 11, 31));
  const totalWeeks = getISOWeekNumber(dec31);

  // Count which month each week belongs to
  for (let week = 1; week <= totalWeeks; week++) {
    const { year: weekYear, month } = getMonthForISOWeek(year, week);
    // Only count weeks that belong to this year's months
    if (weekYear === year && weeksPerMonth[month] !== undefined) {
      weeksPerMonth[month]++;
    }
  }

  // Check if Week 53 of previous year belongs to January of this year
  const prevYearDec31 = new Date(Date.UTC(year - 1, 11, 31));
  const prevYearWeeks = getISOWeekNumber(prevYearDec31);
  if (prevYearWeeks === 53) {
    const { year: w53Year, month: w53Month } = getMonthForISOWeek(year - 1, 53);
    if (w53Year === year && w53Month === 1) {
      weeksPerMonth[1]++;
    }
  }

  return weeksPerMonth;
};

// Weekly base target
const WEEKLY_TARGET = {
  incidentCount: 7,
  mttrAvgMinutes: 30,
  lostHours: 3.6
};

// Calculate monthly targets based on dynamic ISO week count
const getMonthlyTarget = (month: number, year: number): PeriodTarget => {
  const weeksPerMonth = countWeeksPerMonth(year);
  const weeks = weeksPerMonth[month] || 4;
  return {
    incidentCount: WEEKLY_TARGET.incidentCount * weeks,
    mttrAvgMinutes: WEEKLY_TARGET.mttrAvgMinutes,
    lostHours: WEEKLY_TARGET.lostHours * weeks
  };
};

// Calculate quarterly targets (sum of 3 months)
const getQuarterlyTarget = (quarter: number, year: number): PeriodTarget => {
  const monthsInQuarter = [
    [1, 2, 3],   // Q1
    [4, 5, 6],   // Q2
    [7, 8, 9],   // Q3
    [10, 11, 12] // Q4
  ][quarter - 1];

  const weeksPerMonth = countWeeksPerMonth(year);
  const totalWeeks = monthsInQuarter.reduce((sum, m) => sum + weeksPerMonth[m], 0);

  return {
    incidentCount: WEEKLY_TARGET.incidentCount * totalWeeks,
    mttrAvgMinutes: WEEKLY_TARGET.mttrAvgMinutes,
    lostHours: WEEKLY_TARGET.lostHours * totalWeeks
  };
};

// Calculate yearly targets dynamically
const getYearlyTarget = (year: number): PeriodTarget => {
  // Calculate total ISO weeks in the year
  const dec31 = new Date(Date.UTC(year, 11, 31));
  const weeksInYear = getISOWeekNumber(dec31);

  return {
    incidentCount: WEEKLY_TARGET.incidentCount * weeksInYear,
    mttrAvgMinutes: WEEKLY_TARGET.mttrAvgMinutes,
    lostHours: WEEKLY_TARGET.lostHours * weeksInYear
  };
};

const TARGETS: { [key: string]: PeriodTarget } = {
  weekly: WEEKLY_TARGET,
  monthly: getMonthlyTarget(1, 2026), // Default to January 2026, will be calculated dynamically
  quarterly: getQuarterlyTarget(1, 2026), // Default to Q1 2026, will be calculated dynamically
  yearly: getYearlyTarget(2026) // Default to 2026, will be calculated dynamically
};

const getMonthString = (date: Date): string => {
  const weekNum = getISOWeekNumber(date);
  const weekYear = getISOWeekYear(date);

  // Use dynamic ISO week to month mapping
  const { year, month } = getMonthForISOWeek(weekYear, weekNum);
  return `${year}-${String(month).padStart(2, '0')}`;
};

const getQuarterString = (date: Date): string => {
  const weekNum = getISOWeekNumber(date);
  const weekYear = getISOWeekYear(date);

  // Use dynamic ISO week to month mapping
  const { year, month } = getMonthForISOWeek(weekYear, weekNum);
  const quarter = Math.floor((month - 1) / 3) + 1;
  return `${year}-Q${quarter}`;
};

const getYearString = (date: Date): string => {
  const weekYear = getISOWeekYear(date);
  return `${weekYear}`;
};

const parseISODate = (isoStr: string): Date => {
  return new Date(isoStr);
};

// ─── Main Component ──────────────────────────────────────────────────────────
const IncidentOperationsView: React.FC = () => {
  // ─── State ─────────────────────────────────────────────────────────────────
  const { accounts } = useMsal();

  // Resolve current user identity: MSAL account → JWT decode → localStorage → 'unknown'
  const getChangedBy = (): string => {
    if (accounts && accounts.length > 0) {
      const u = accounts[0];
      return u.username || u.name || 'unknown';
    }
    // Fallback: JWT-decoded token
    const fromToken = getCurrentUser();
    if (fromToken !== 'unknown') return fromToken;
    // Fallback: localStorage test-user
    try {
      const stored = localStorage.getItem('maersk_user');
      if (stored) {
        const u = JSON.parse(stored);
        return u.username || u.email || u.name || 'unknown';
      }
    } catch { /* ignore */ }
    return 'unknown';
  };

  // KPI edit access: only email addresses listed in REACT_APP_KPI_EDIT_ALLOWED_EMAILS
  const canEditKPI = (() => {
    const raw = process.env.REACT_APP_KPI_EDIT_ALLOWED_EMAILS || '';
    if (!raw.trim()) return true; // if env var not set, allow all (dev fallback)
    const allowed = raw.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    const currentUser = getChangedBy().toLowerCase();
    return allowed.includes(currentUser);
  })();

  // OOP edit access: specific users who can edit OOP field
  const canEditOOP = (() => {
    const allowedUsers = [
      'raghul.sridharan@maersk.com',
      'mahesh.s.bhosale@maersk.com',
      'k.harish@maersk.com',
      'shilpitha.swarna@maersk.com',
      'nilesh.chakkar@maersk.com',
      'vaishali.parande@maersk.com',
      'nitish.yn@maersk.com'
    ].map(e => e.toLowerCase());
    const currentUser = getChangedBy().toLowerCase();
    return allowedUsers.includes(currentUser);
  })();

  const [data, setData] = useState<IncidentRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Period type: weekly or monthly
  const [periodType, setPeriodType] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');

  // Time range selection
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('ytd');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [filterStartDate, setFilterStartDate] = useState<string | undefined>(undefined);
  const [filterEndDate, setFilterEndDate] = useState<string | undefined>(undefined);

  // Hierarchy filters
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);

  // OOP exclusion toggle (default: exclude OOP incidents)
  const [excludeOOP, setExcludeOOP] = useState<boolean>(true);

  // KPI selection (default: Hours Lost to Incidents only)
  const [selectedKPIs, setSelectedKPIs] = useState<string[]>(['Lost Hours']);

  // View configuration
  const [viewBy, setViewBy] = useState<'Portfolio' | 'Platform' | 'Application'>('Portfolio');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [filtersCollapsed, setFiltersCollapsed] = useState<boolean>(false);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const headerRowTopRef = useRef<HTMLTableRowElement | null>(null);

  // Dialog for incident details
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCellData, setSelectedCellData] = useState<{ node: HierarchyNode; period: string } | null>(null);
  const [dialogPage, setDialogPage] = useState(0);
  const [dialogRowsPerPage, setDialogRowsPerPage] = useState(10);
  
  // OOP editing state (using incident number as key since record.id is undefined)
  const [editingOOPIncidentNumber, setEditingOOPIncidentNumber] = useState<string | null>(null);
  const [oopValues, setOopValues] = useState<Map<string, string>>(new Map());
  const [savingOOP, setSavingOOP] = useState(false);

  // MTTR and MTTD state
  const [mttrData, setMttrData] = useState<Map<string, number>>(new Map());
  const [mttdData, setMttdData] = useState<Map<string, number>>(new Map());
  const [mttrLoading, setMttrLoading] = useState(false);

  // ─── Editable Targets & Forecast ───────────────────────────────────────────
  // Each KPI has a weekly base (fallback) + per-period-key overrides.
  // Override keys match period keys used in the table: "2026-W17", "2026-04", "2026-Q2", "2026"
  // null override value means "use auto-scaled base"
  interface PeriodOverride { target: number | null; forecast: number | null; }
  interface KPIEditConfig {
    weeklyBaseTarget: number;
    weeklyBaseForecast: number;
    overrides: Record<string, PeriodOverride>; // periodKey → { target, forecast }
  }
  type EditableKPIConfig = Record<'incidentCount' | 'lostHours' | 'mttr', KPIEditConfig>;

  const defaultEditableTargets: EditableKPIConfig = {
    incidentCount: { weeklyBaseTarget: 7, weeklyBaseForecast: 7, overrides: {} },
    lostHours: { weeklyBaseTarget: 3.6, weeklyBaseForecast: 12, overrides: {} },
    mttr: { weeklyBaseTarget: 30, weeklyBaseForecast: 30, overrides: {} },
  };

  const [editableTargets, setEditableTargets] = useState<EditableKPIConfig>(defaultEditableTargets);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDraft, setEditDraft] = useState<EditableKPIConfig>(defaultEditableTargets);
  const [editKpiTab, setEditKpiTab] = useState(0);
  const [editPeriodTab, setEditPeriodTab] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsSaving, setTargetsSaving] = useState(false);

  // Map API metric_id → local KPI key
  const METRIC_ID_MAP: Record<string, keyof EditableKPIConfig> = {
    incident_count: 'incidentCount',
    lost_hours: 'lostHours',
    mttr: 'mttr',
  };
  const LOCAL_TO_METRIC_ID: Record<keyof EditableKPIConfig, string> = {
    incidentCount: 'incident_count',
    lostHours: 'lost_hours',
    mttr: 'mttr',
  };

  // ─── Load saved targets from backend on mount ───────────────────────────────
  useEffect(() => {
    const loadTargets = async () => {
      setTargetsLoading(true);
      try {
        const res = await authFetch(KPI_TARGETS.BASE);
        if (!res.ok) return; // silently fall back to defaults on error
        const json = await res.json();

        setEditableTargets(prev => {
          const next: EditableKPIConfig = {
            incidentCount: { ...prev.incidentCount, overrides: {} },
            lostHours: { ...prev.lostHours, overrides: {} },
            mttr: { ...prev.mttr, overrides: {} },
          };

          // Apply base rows
          (json.base || []).forEach((row: any) => {
            const key = METRIC_ID_MAP[row.metric_id];
            if (key) {
              next[key].weeklyBaseTarget = parseFloat(row.weekly_target);
              next[key].weeklyBaseForecast = parseFloat(row.weekly_forecast);
            }
          });

          // Apply override rows
          (json.overrides || []).forEach((row: any) => {
            const key = METRIC_ID_MAP[row.metric_id];
            if (key) {
              next[key].overrides[row.period_key] = {
                target: row.target_value != null ? parseFloat(row.target_value) : null,
                forecast: row.forecast_value != null ? parseFloat(row.forecast_value) : null,
              };
            }
          });

          return next;
        });
      } catch {
        // Network error — keep defaults, non-blocking
      } finally {
        setTargetsLoading(false);
      }
    };
    loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log OOP access information
  useEffect(() => {
    const currentUser = getChangedBy();
    console.log('🔐 OOP Access Check:', {
      currentUser,
      canEditOOP,
      allowedUsers: [
        'raghul.sridharan@maersk.com',
        'mahesh.s.bhosale@maersk.com',
        'k.harish@maersk.com',
        'shilpitha.swarna@maersk.com',
        'nilesh.chakkar@maersk.com',
        'vaishali.parande@maersk.com',
        'nitish.yn@maersk.com'
      ]
    });
  }, [canEditOOP]);

  // ─── OOP Display Mapping ───────────────────────────────────────────────────
  // Maps database OOP value to UI display value
  const getOOPDisplayValue = (oopValue: string | undefined): string => {
    if (!oopValue || oopValue.trim() === '') return '';
    if (oopValue.toLowerCase() === 'manual') return 'Yes';
    return oopValue; // Show other values as-is
  };

  // Maps UI selection to database value
  const getOOPDatabaseValue = (selection: string): string => {
    if (selection === 'Yes') return 'manual';
    if (selection === 'No') return '';
    return selection;
  };

  // ─── Save OOP Value ────────────────────────────────────────────────────────
  const saveOOPValue = async (incidentNumber: string, newValue: string) => {
    setSavingOOP(true);
    try {
      const dbValue = getOOPDatabaseValue(newValue);
      console.log(`Saving OOP for ${incidentNumber}: UI="${newValue}" → DB="${dbValue}"`);
      
      const response = await authFetch(`${INCIDENTS.BASE}/${incidentNumber}/oop`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oop_value: dbValue })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `Failed to update OOP: ${response.statusText}`);
      }

      // Update local state
      setOopValues(prev => {
        const updated = new Map(prev);
        updated.set(incidentNumber, dbValue);
        return updated;
      });

      // Update data state to reflect the change
      setData(prevData => prevData.map(record => 
        record.number === incidentNumber ? { ...record, oop: dbValue } : record
      ));

      console.log('✅ OOP value saved successfully');
    } catch (err) {
      console.error('❌ Error saving OOP value:', err);
      alert(`Failed to save OOP value: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSavingOOP(false);
      setEditingOOPIncidentNumber(null);
    }
  };

  // ─── Export Dialog Incidents to CSV ────────────────────────────────────────
  const exportDialogIncidentsToCSV = (incidents: IncidentRecord[]) => {
    // Helper function to escape CSV values
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const headers = ['Incident ID', 'Affected Portfolio', 'Affected Platform', 'Affected App', 'Title', 'Priority', 'Created At', 'Resolved At', 'MTTR', 'OOP'];
    const rows = incidents.map(record => [
      escapeCSV(record.number),
      escapeCSV(record.portfolio),
      escapeCSV(record.platform),
      escapeCSV(record.application),
      escapeCSV(record.title),
      escapeCSV(record.priority),
      escapeCSV(new Date(record.createdDate).toLocaleString()),
      escapeCSV(record.resolvedDate ? new Date(record.resolvedDate).toLocaleString() : '-'),
      record.hasMttrData && record.mttr !== undefined ? (record.mttr / 3600).toFixed(2) + 'h' : 'N/A',
      escapeCSV(record.oop || '-')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `incidents_${selectedCellData?.node.name || 'data'}_${selectedCellData?.period || 'all'}.csv`;
    link.click();
  };

  // ─── Fetch MTTR Data ───────────────────────────────────────────────────────
  const fetchMTTRData = async (): Promise<{ mttrMap: Map<string, number>; mttdMap: Map<string, number> }> => {
    try {
      setMttrLoading(true);
      console.log('🔄 Fetching MTTR and MTTD data for all incidents');

      const response = await authFetch(INCIDENTS.MTTR, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch MTTR data: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📦 MTTR/MTTD API Response:', result);

      if (result.data && Array.isArray(result.data)) {
        const mttrMap = new Map<string, number>();
        const mttdMap = new Map<string, number>();
        
        result.data.forEach((item: MTTRData) => {
          const mttrSeconds = parseFloat(item.mttr);
          const mttdSeconds = parseFloat(item.mttd);
          
          if (!isNaN(mttrSeconds)) {
            mttrMap.set(item.number, mttrSeconds);
          }
          
          if (!isNaN(mttdSeconds)) {
            mttdMap.set(item.number, mttdSeconds);
          }
        });
        
        setMttrData(mttrMap);
        setMttdData(mttdMap);
        console.log('✅ MTTR data loaded for', mttrMap.size, 'incidents from API');
        console.log('✅ MTTD data loaded for', mttdMap.size, 'incidents from API');
        console.log('📊 Sample MTTR data:', Array.from(mttrMap.entries()).slice(0, 5));
        console.log('📊 Sample MTTD data:', Array.from(mttdMap.entries()).slice(0, 5));
        return { mttrMap, mttdMap };
      } else {
        console.warn('⚠️ MTTR API returned no data or invalid format');
        return { mttrMap: new Map<string, number>(), mttdMap: new Map<string, number>() };
      }
    } catch (err) {
      console.error('❌ Error fetching MTTR data:', err);
      return { mttrMap: new Map<string, number>(), mttdMap: new Map<string, number>() };
    } finally {
      setMttrLoading(false);
    }
  };

  // ─── Data Fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all P1/P2 incidents from 2025-01-01 to today
        const todayStr = formatLocalDate(new Date());
        const response = await authFetch(INCIDENTS.P1_P2_WITH_TRENDS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ start_date: '2025-01-01', end_date: todayStr, page: 1, pageSize: 20000 })
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch P1/P2 incidents (${response.status})`);
        }

        const json = await response.json();
        const rawIncidents: any[] = (json.success && json.data?.incidents) ? json.data.incidents : [];

        console.log('Fetched incidents:', rawIncidents.length);

        // Fetch MTTR and MTTD data from separate API
        const { mttrMap, mttdMap } = await fetchMTTRData();
        console.log('🔍 MTTR Map size:', mttrMap.size);
        console.log('🔍 MTTD Map size:', mttdMap.size);

        // Transform to IncidentRecord format
        const records: IncidentRecord[] = rawIncidents.map((inc: any) => {
          const priority = inc.priority === '1' || inc.priority === 1 ? 'P1' : 'P2';
          const incidentNumber = inc.number;
          const mttrFromApi = mttrMap.get(incidentNumber);
          const mttdFromApi = mttdMap.get(incidentNumber);

          return {
            id: inc.id || inc.sys_id,
            number: incidentNumber,
            title: inc.short_description || inc.title,
            priority: priority,
            portfolio: inc.portfolio_name || 'Unknown',
            platform: inc.platform_name || 'Unknown',
            application: inc.cmdb_ci_display_value || 'Unknown',
            createdDate: inc.sys_created_on_value || inc.createdDate,
            resolvedDate: inc.resolved_at || inc.resolvedDate,
            status: inc.state_display_value || inc.state || inc.status || 'Unknown',
            mttr: mttrFromApi,
            mttd: mttdFromApi,
            hasMttrData: mttrFromApi !== undefined,
            hasMttdData: mttdFromApi !== undefined,
            outage_duration: inc.outage_duration,
            oop: inc.oop || ''
          };
        });

        console.log('✅ Transformed records:', records.length);
        setData(records);
      } catch (err: any) {
        console.error('Error fetching incident data:', err);
        setError(err.message || 'Failed to fetch incident data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ─── Date Range Effect ─────────────────────────────────────────────────────
  useEffect(() => {
    const now = new Date();
    const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    let startDate = new Date(endDate);

    switch (selectedTimeRange) {
      case '7d': {
        startDate.setUTCDate(startDate.getUTCDate() - 7);
        break;
      }
      case '30d': {
        startDate.setUTCDate(startDate.getUTCDate() - 30);
        break;
      }
      case '90d': {
        startDate.setUTCDate(startDate.getUTCDate() - 90);
        break;
      }
      case 'ytd': {
        startDate = new Date(Date.UTC(endDate.getUTCFullYear(), 0, 1));
        break;
      }
      case 'custom': {
        if (customDateRange.start && customDateRange.end) {
          setFilterStartDate(customDateRange.start);
          setFilterEndDate(customDateRange.end);
        }
        return;
      }
    }

    if (selectedTimeRange !== 'custom') {
      setFilterStartDate(formatLocalDate(startDate));
      setFilterEndDate(formatLocalDate(endDate));
    }
  }, [selectedTimeRange, customDateRange]);

  const clearAllFilters = () => {
    setPeriodType('weekly');
    setSelectedTimeRange('ytd');
    setShowCustomDatePicker(false);
    setCustomDateRange({ start: '', end: '' });
    setSelectedPortfolios([]);
    setSelectedPlatforms([]);
    setSelectedApplications([]);
    setSelectedKPIs(['Incident Count', 'Lost Hours']);
    setExcludeOOP(true);
    setViewBy('Portfolio');
    setExpandedNodes(new Set());
  };

  // ─── Derived: Min/Max dates from data ──────────────────────────────────────
  const dataDateRange = useMemo(() => {
    if (data.length === 0) {
      const today = new Date();
      const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      return { min: formatLocalDate(todayUTC), max: formatLocalDate(todayUTC) };
    }
    const dates = data.filter(r => r.resolvedDate).map(r => new Date(r.resolvedDate!).getTime());
    return {
      min: dates.length ? formatLocalDate(new Date(Math.min(...dates))) : formatLocalDate(new Date()),
      max: formatLocalDate(new Date())
    };
  }, [data]);

  // ─── Derived: Dynamic filter options ───────────────────────────────────────
  const availablePortfolios = useMemo(() => {
    return Array.from(new Set(data.map(r => r.portfolio))).sort();
  }, [data]);

  const availablePlatforms = useMemo(() => {
    let filtered = data;
    if (selectedPortfolios.length > 0) filtered = filtered.filter(r => selectedPortfolios.includes(r.portfolio));
    return Array.from(new Set(filtered.map(r => r.platform))).sort();
  }, [data, selectedPortfolios]);

  const availableApplications = useMemo(() => {
    let filtered = data;
    if (selectedPortfolios.length > 0) filtered = filtered.filter(r => selectedPortfolios.includes(r.portfolio));
    if (selectedPlatforms.length > 0) filtered = filtered.filter(r => selectedPlatforms.includes(r.platform));
    return Array.from(new Set(filtered.map(r => r.application))).sort();
  }, [data, selectedPortfolios, selectedPlatforms]);

  // ─── Select All helper for Autocomplete ────────────────────────────────────
  const makeSelectAllAutocomplete = (
    label: string,
    options: string[],
    selected: string[],
    setSelected: (val: string[]) => void,
    onClearDownstream?: () => void,
  ) => {
    const allOptions = [SELECT_ALL_OPTION, ...options];
    const isAllSelected = options.length > 0 && selected.length === options.length;

    return (
      <Autocomplete
        multiple
        size="small"
        limitTags={1}
        options={allOptions}
        value={isAllSelected ? [SELECT_ALL_OPTION, ...selected] : selected}
        onChange={(_, newValue) => {
          const hasSelectAll = newValue.includes(SELECT_ALL_OPTION);
          const prevHasSelectAll = isAllSelected;
          if (hasSelectAll && !prevHasSelectAll) {
            setSelected([...options]);
          } else if (!hasSelectAll && prevHasSelectAll) {
            setSelected([]);
          } else {
            const filtered = newValue.filter(v => v !== SELECT_ALL_OPTION);
            setSelected(filtered);
            if (filtered.length < selected.length && onClearDownstream) {
              onClearDownstream();
            }
          }
        }}
        disableCloseOnSelect
        getOptionLabel={option => option}
        renderOption={(props, option, { selected: optionSelected }) => (
          <li {...props}>
            <Checkbox checked={option === SELECT_ALL_OPTION ? isAllSelected : optionSelected} sx={{ mr: 1 }} />
            <ListItemText primary={option} />
          </li>
        )}
        renderInput={params => <TextField {...params} label={label} />}
        sx={{ 
          minWidth: 200,
          '& .MuiAutocomplete-tag': {
            display: 'flex',
            alignItems: 'center',
            height: '24px',
            margin: '2px'
          },
          '& .MuiChip-root': {
            height: '24px',
            display: 'flex',
            alignItems: 'center'
          },
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: '24px',
            padding: '0 8px'
          },
          '& .MuiAutocomplete-input': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      />
    );
  };

  // ─── Filtered Data ─────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let filtered = data;

    // Date range filter
    if (filterStartDate && filterEndDate) {
      const start = new Date(filterStartDate).getTime();
      const end = new Date(filterEndDate).getTime();
      filtered = filtered.filter(r => {
        if (!r.resolvedDate) return false;
        const t = new Date(r.resolvedDate).getTime();
        return t >= start && t <= end;
      });
    }

    // Hierarchy filters
    if (selectedPortfolios.length > 0) filtered = filtered.filter(r => selectedPortfolios.includes(r.portfolio));
    if (selectedPlatforms.length > 0) filtered = filtered.filter(r => selectedPlatforms.includes(r.platform));
    if (selectedApplications.length > 0) filtered = filtered.filter(r => selectedApplications.includes(r.application));

    // OOP filter
    if (excludeOOP) filtered = filtered.filter(r => !r.oop || r.oop.trim() === '');

    return filtered;
  }, [data, filterStartDate, filterEndDate, selectedPortfolios, selectedPlatforms, selectedApplications, excludeOOP]);

  // ─── Build Hierarchy ───────────────────────────────────────────────────────
  const hierarchyData = useMemo(() => {
    const buildMetrics = (records: IncidentRecord[]): MetricsByPeriod => {
      const metrics: MetricsByPeriod = {};
      records.forEach(r => {
        if (!r.resolvedDate) return;
        const date = parseISODate(r.resolvedDate);
        let periodKey: string = '';
        if (periodType === 'weekly') {
          const weekNum = getISOWeekNumber(date);
          const weekYear = getISOWeekYear(date);
          periodKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
        } else if (periodType === 'monthly') {
          // Use calendar month directly to match the period generation logic
          const y = date.getUTCFullYear();
          const m = date.getUTCMonth() + 1;
          periodKey = `${y}-${String(m).padStart(2, '0')}`;
        } else if (periodType === 'quarterly') {
          periodKey = getQuarterString(date);
        } else if (periodType === 'yearly') {
          periodKey = getYearString(date);
        }

        if (!metrics[periodKey]) {
          metrics[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
        }

        metrics[periodKey].incidentCount++;

        // Add MTTR if available (in seconds)
        if (r.mttr) {
          metrics[periodKey].totalMTTRSeconds += r.mttr;
          metrics[periodKey].lostHours += r.mttr / 3600;
        }
      });
      return metrics;
    };

    const portfolioMap: { [key: string]: HierarchyNode } = {};

    filteredData.forEach(record => {
      const { portfolio, platform, application } = record;

      // Build portfolio node
      if (!portfolioMap[portfolio]) {
        portfolioMap[portfolio] = {
          name: portfolio,
          level: 'portfolio',
          metrics: {},
          records: [],
          children: []
        };
      }
      portfolioMap[portfolio].records.push(record);

      // Build platform node
      let platformNode = portfolioMap[portfolio].children!.find(p => p.name === platform);
      if (!platformNode) {
        platformNode = {
          name: platform,
          level: 'platform',
          metrics: {},
          records: [],
          children: []
        };
        portfolioMap[portfolio].children!.push(platformNode);
      }
      platformNode.records.push(record);

      // Build application node
      let appNode = platformNode.children!.find(a => a.name === application);
      if (!appNode) {
        appNode = {
          name: application,
          level: 'application',
          metrics: {},
          records: [],
        };
        platformNode.children!.push(appNode);
      }
      appNode.records.push(record);
    });

    // Calculate metrics for all nodes
    const calculateNodeMetrics = (node: HierarchyNode) => {
      node.metrics = buildMetrics(node.records);
      if (node.children) {
        node.children.forEach(calculateNodeMetrics);
        node.children.sort((a, b) => a.name.localeCompare(b.name));
      }
    };

    const portfolioList = Object.values(portfolioMap);
    portfolioList.forEach(calculateNodeMetrics);
    portfolioList.sort((a, b) => a.name.localeCompare(b.name));

    return portfolioList;
  }, [filteredData, periodType]);

  // ─── Initialize expanded nodes - default collapsed (portfolio level only) ──
  // Removed auto-expansion logic - hierarchy starts collapsed by default

  // ─── Periods to Display ────────────────────────────────────────────────────
  const periodsToDisplay = useMemo(() => {
    if (filteredData.length === 0) return [];

    // Get the date range from filteredData (using resolvedDate for period grouping)
    const dates = filteredData.filter(r => r.resolvedDate).map(r => parseISODate(r.resolvedDate!));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

    const periodSet = new Set<string>();

    // Generate all periods in the date range
    if (periodType === 'weekly') {
      // For weekly view, generate ALL consecutive weeks between min and max week numbers
      // to avoid skipping weeks with no incidents
      const minWeek = getISOWeekNumber(minDate);
      const minYear = getISOWeekYear(minDate);
      const maxWeek = getISOWeekNumber(maxDate);
      const maxYear = getISOWeekYear(maxDate);
      
      let currentYear = minYear;
      let currentWeek = minWeek;
      
      while (currentYear < maxYear || (currentYear === maxYear && currentWeek <= maxWeek)) {
        periodSet.add(`${currentYear}-W${String(currentWeek).padStart(2, '0')}`);
        
        // Move to next week
        currentWeek++;
        // Get the number of weeks in the current year (usually 52, sometimes 53)
        const lastDayOfYear = new Date(Date.UTC(currentYear, 11, 31));
        const lastWeekOfYear = getISOWeekNumber(lastDayOfYear);
        if (currentWeek > lastWeekOfYear) {
          currentWeek = 1;
          currentYear++;
        }
      }
    } else {
      // For other view modes, use date iteration
      const currentDate = new Date(minDate);
      while (currentDate <= maxDate) {
        if (periodType === 'monthly') {
          // Use calendar month directly to avoid ISO week mapping issues
          // that skip months when the 1st falls in a week belonging to another month
          const calendarMonth = `${currentDate.getUTCFullYear()}-${String(currentDate.getUTCMonth() + 1).padStart(2, '0')}`;
          periodSet.add(calendarMonth);
          // Move to next month
          currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
        } else if (periodType === 'quarterly') {
          periodSet.add(getQuarterString(currentDate));
          // Move to next quarter
          currentDate.setUTCMonth(currentDate.getUTCMonth() + 3);
        } else if (periodType === 'yearly') {
          periodSet.add(getYearString(currentDate));
          // Move to next year
          currentDate.setUTCFullYear(currentDate.getUTCFullYear() + 1);
        }
      }
    }

    // Ensure current period is visible even if no incidents exist for it
    const now = new Date();
    let currentPeriod = '';
    if (periodType === 'weekly') {
      const w = getISOWeekNumber(now);
      const y = getISOWeekYear(now);
      currentPeriod = `${y}-W${String(w).padStart(2, '0')}`;
    } else if (periodType === 'monthly') {
      // Use calendar month directly to match the period generation logic
      const y = now.getUTCFullYear();
      const m = now.getUTCMonth() + 1;
      currentPeriod = `${y}-${String(m).padStart(2, '0')}`;
    } else if (periodType === 'quarterly') {
      const y = getISOWeekYear(now);
      const m = getMonthForISOWeek(y, getISOWeekNumber(now)).month;
      const q = Math.floor((m - 1) / 3) + 1;
      currentPeriod = `${y}-Q${q}`;
    } else if (periodType === 'yearly') {
      currentPeriod = String(getISOWeekYear(now));
    }

    if (currentPeriod) periodSet.add(currentPeriod);

    return Array.from(periodSet).sort();
  }, [filteredData, periodType]);

  // Scroll the current period into view when periods change
  useEffect(() => {
    const now = new Date();
    let currentPeriodKey = '';
    if (periodType === 'weekly') {
      const w = getISOWeekNumber(now);
      const y = getISOWeekYear(now);
      currentPeriodKey = `${y}-W${String(w).padStart(2, '0')}`;
    } else if (periodType === 'monthly') {
      // Use calendar month directly to match the period generation logic
      const y = now.getUTCFullYear();
      const m = now.getUTCMonth() + 1;
      currentPeriodKey = `${y}-${String(m).padStart(2, '0')}`;
    } else if (periodType === 'quarterly') {
      const y = getISOWeekYear(now);
      const m = getMonthForISOWeek(y, getISOWeekNumber(now)).month;
      const q = Math.floor((m - 1) / 3) + 1;
      currentPeriodKey = `${y}-Q${q}`;
    } else if (periodType === 'yearly') {
      currentPeriodKey = String(getISOWeekYear(now));
    }

    // Wait for DOM to update
    setTimeout(() => {
      if (!currentPeriodKey) return;
      const el = document.querySelector(`[data-period="${currentPeriodKey}"]`) as HTMLElement | null;
      if (el && tableContainerRef.current) {
        try {
          // Scroll the container so the current period column is visible
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } catch (e) {
          // ignore
        }
      }
    }, 50);
  }, [periodsToDisplay, periodType]);

  // (sticky row offset measurement removed — new column-based layout does not need it)

  // ─── Data filtered by hierarchy only (no date filter, for PY comparison) ───
  const hierarchyFilteredData = useMemo(() => {
    let filtered = data;
    if (selectedPortfolios.length > 0) filtered = filtered.filter(r => selectedPortfolios.includes(r.portfolio));
    if (selectedPlatforms.length > 0) filtered = filtered.filter(r => selectedPlatforms.includes(r.platform));
    if (selectedApplications.length > 0) filtered = filtered.filter(r => selectedApplications.includes(r.application));
    if (excludeOOP) filtered = filtered.filter(r => !r.oop || r.oop.trim() === '');
    return filtered;
  }, [data, selectedPortfolios, selectedPlatforms, selectedApplications, excludeOOP]);

  // ─── Calculate Totals ──────────────────────────────────────────────────────
  // Uses hierarchyFilteredData (no date-range filter) so that overlapping periods
  // at the edge of the selected time range show the full period count, not a partial one.
  const totals = useMemo(() => {
    const periodTotals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
    const grandTotal = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };

    hierarchyFilteredData.forEach(r => {
      if (!r.resolvedDate) return;
      const date = parseISODate(r.resolvedDate);
      let periodKey: string = '';
      if (periodType === 'weekly') {
        const weekNum = getISOWeekNumber(date);
        const weekYear = getISOWeekYear(date);
        periodKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
      } else if (periodType === 'monthly') {
        // Use calendar month directly to match the period generation logic
        const y = date.getUTCFullYear();
        const m = date.getUTCMonth() + 1;
        periodKey = `${y}-${String(m).padStart(2, '0')}`;
      } else if (periodType === 'quarterly') {
        periodKey = getQuarterString(date);
      } else if (periodType === 'yearly') {
        periodKey = getYearString(date);
      }

      if (!periodTotals[periodKey]) {
        periodTotals[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
      }

      periodTotals[periodKey].incidentCount++;
      grandTotal.incidentCount++;

      if (r.mttr) {
        periodTotals[periodKey].totalMTTRSeconds += r.mttr;
        periodTotals[periodKey].lostHours += r.mttr / 3600;
        grandTotal.totalMTTRSeconds += r.mttr;
        grandTotal.lostHours += r.mttr / 3600;
      }
    });

    return { periodTotals, grandTotal };
  }, [hierarchyFilteredData, periodType]);

  // ─── Previous Year Totals (same period key, year - 1) ──────────────────────
  const prevYearTotals = useMemo(() => {
    const periodTotals: { [period: string]: { incidentCount: number; totalMTTRSeconds: number; lostHours: number } } = {};
    hierarchyFilteredData.forEach(r => {
      if (!r.resolvedDate) return;
      const date = parseISODate(r.resolvedDate);
      let periodKey = '';
      if (periodType === 'weekly') {
        const weekNum = getISOWeekNumber(date);
        const weekYear = getISOWeekYear(date);
        periodKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
      } else if (periodType === 'monthly') {
        // Use calendar month directly to match the period generation logic
        const y = date.getUTCFullYear();
        const m = date.getUTCMonth() + 1;
        periodKey = `${y}-${String(m).padStart(2, '0')}`;
      } else if (periodType === 'quarterly') {
        periodKey = getQuarterString(date);
      } else if (periodType === 'yearly') {
        periodKey = getYearString(date);
      }
      if (!periodTotals[periodKey]) {
        periodTotals[periodKey] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
      }
      periodTotals[periodKey].incidentCount++;
      if (r.mttr) {
        periodTotals[periodKey].totalMTTRSeconds += r.mttr;
        periodTotals[periodKey].lostHours += r.mttr / 3600;
      }
    });
    return periodTotals;
  }, [hierarchyFilteredData, periodType]);

  // ─── Shift a period key back by one year ──────────────────────────────────
  const getPrevYearPeriodKey = (period: string): string => {
    return period.replace(/^(\d{4})/, (_, y) => String(parseInt(y, 10) - 1));
  };

  // ─── Calculate Current vs Previous Period Comparison ──────────────────────
  const periodComparison = useMemo(() => {
    const now = new Date();
    let currentPeriodKey = '';
    let previousPeriodKey = '';

    if (periodType === 'weekly') {
      const currentWeek = getISOWeekNumber(now);
      const currentYear = now.getUTCFullYear();
      currentPeriodKey = `${currentYear}-W${String(currentWeek).padStart(2, '0')}`;

      // Previous week
      const prevWeekDate = new Date(now);
      prevWeekDate.setDate(prevWeekDate.getDate() - 7);
      const prevWeek = getISOWeekNumber(prevWeekDate);
      const prevYear = prevWeekDate.getUTCFullYear();
      previousPeriodKey = `${prevYear}-W${String(prevWeek).padStart(2, '0')}`;
    } else if (periodType === 'monthly') {
      currentPeriodKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;

      // Previous month
      const prevMonth = now.getUTCMonth() === 0 ? 12 : now.getUTCMonth();
      const prevYear = now.getUTCMonth() === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
      previousPeriodKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
    } else if (periodType === 'quarterly') {
      const currentQuarter = Math.floor(now.getUTCMonth() / 3) + 1;
      currentPeriodKey = `${now.getUTCFullYear()}-Q${currentQuarter}`;

      // Previous quarter
      const prevQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
      const prevYear = currentQuarter === 1 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
      previousPeriodKey = `${prevYear}-Q${prevQuarter}`;
    } else if (periodType === 'yearly') {
      currentPeriodKey = `${now.getUTCFullYear()}`;
      previousPeriodKey = `${now.getUTCFullYear() - 1}`;
    }

    const current = totals.periodTotals[currentPeriodKey] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
    const previous = totals.periodTotals[previousPeriodKey] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };

    return { current, previous, currentPeriodKey, previousPeriodKey };
  }, [totals, periodType]);

  // ─── Format Period Label ───────────────────────────────────────────────────
  const formatPeriod = (period: string): string => {
    if (periodType === 'weekly') {
      const parts = period.split('-W');
      return `Week ${parts[1]} - ${parts[0]}`;
    } else if (periodType === 'monthly') {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else if (periodType === 'quarterly') {
      const parts = period.split('-');
      return `${parts[1]} - ${parts[0]}`; // "Q1 - 2026"
    } else if (periodType === 'yearly') {
      return period; // Already formatted as "2026"
    }
    return period;
  };

  // ─── Get Period Date Range for Tooltip ────────────────────────────────────
  const getPeriodDateRange = (period: string): string => {
    if (periodType === 'weekly') {
      const parts = period.split('-W');
      const year = parseInt(parts[0]);
      const week = parseInt(parts[1]);

      // Calculate the start date of the ISO week
      const jan4 = new Date(Date.UTC(year, 0, 4));
      const dayOfWeek = jan4.getUTCDay() || 7;
      const startOfYear = new Date(jan4);
      startOfYear.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

      const startDate = new Date(startOfYear);
      startDate.setUTCDate(startOfYear.getUTCDate() + (week - 1) * 7);

      const endDate = new Date(startDate);
      endDate.setUTCDate(startDate.getUTCDate() + 6);

      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (periodType === 'monthly') {
      const [yearStr, monthStr] = period.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);

      // Dynamic ISO week mapping for any year
      const dec31 = new Date(Date.UTC(year, 11, 31));
      const totalWeeks = getISOWeekNumber(dec31);

      // Find all ISO weeks for this month
      const weeksInMonth: number[] = [];
      for (let week = 1; week <= totalWeeks; week++) {
        const { year: weekYear, month: weekMonth } = getMonthForISOWeek(year, week);
        if (weekYear === year && weekMonth === month) {
          weeksInMonth.push(week);
        }
      }

      // Check if Week 53 of previous year belongs to this January
      if (month === 1) {
        const prevYearDec31 = new Date(Date.UTC(year - 1, 11, 31));
        const prevYearWeeks = getISOWeekNumber(prevYearDec31);
        if (prevYearWeeks === 53) {
          const { year: w53Year, month: w53Month } = getMonthForISOWeek(year - 1, 53);
          if (w53Year === year && w53Month === 1) {
            weeksInMonth.unshift(53);
          }
        }
      }

      if (weeksInMonth.length > 0) {
        weeksInMonth.sort((a, b) => a - b);
        const firstWeek = weeksInMonth[0];
        const lastWeek = weeksInMonth[weeksInMonth.length - 1];

        // Calculate start date of first week
        const firstWeekYear = firstWeek > 52 ? year - 1 : year;
        const jan4First = new Date(Date.UTC(firstWeekYear, 0, 4));
        const dayOfWeekFirst = jan4First.getUTCDay() || 7;
        const startOfYearFirst = new Date(jan4First);
        startOfYearFirst.setUTCDate(jan4First.getUTCDate() - dayOfWeekFirst + 1);
        const startDate = new Date(startOfYearFirst);
        startDate.setUTCDate(startOfYearFirst.getUTCDate() + (firstWeek - 1) * 7);

        // Calculate end date of last week
        const jan4Last = new Date(Date.UTC(year, 0, 4));
        const dayOfWeekLast = jan4Last.getUTCDay() || 7;
        const startOfYearLast = new Date(jan4Last);
        startOfYearLast.setUTCDate(jan4Last.getUTCDate() - dayOfWeekLast + 1);
        const lastWeekStart = new Date(startOfYearLast);
        lastWeekStart.setUTCDate(startOfYearLast.getUTCDate() + (lastWeek - 1) * 7);
        const endDate = new Date(lastWeekStart);
        endDate.setUTCDate(lastWeekStart.getUTCDate() + 6);

        const firstWeekLabel = firstWeek > 52 ? `W${firstWeek}-${firstWeekYear}` : 'W' + String(firstWeek).padStart(2, '0');
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (ISO Weeks: ${firstWeekLabel}-W${String(lastWeek).padStart(2, '0')})`;
      }

      // Fallback to calendar month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (periodType === 'quarterly') {
      const [yearStr, q] = period.split('-Q');
      const year = parseInt(yearStr);
      const quarter = parseInt(q);

      const monthsInQuarter = [
        [1, 2, 3],   // Q1
        [4, 5, 6],   // Q2
        [7, 8, 9],   // Q3
        [10, 11, 12] // Q4
      ][quarter - 1];

      // Dynamic ISO week mapping for any year
      const dec31 = new Date(Date.UTC(year, 11, 31));
      const totalWeeks = getISOWeekNumber(dec31);

      // Find all ISO weeks for this quarter
      const weeksInQuarter: number[] = [];
      for (let week = 1; week <= totalWeeks; week++) {
        const { year: weekYear, month: weekMonth } = getMonthForISOWeek(year, week);
        if (weekYear === year && monthsInQuarter.includes(weekMonth)) {
          weeksInQuarter.push(week);
        }
      }

      // Check if Week 53 of previous year belongs to Q1 of this year
      if (quarter === 1) {
        const prevYearDec31 = new Date(Date.UTC(year - 1, 11, 31));
        const prevYearWeeks = getISOWeekNumber(prevYearDec31);
        if (prevYearWeeks === 53) {
          const { year: w53Year, month: w53Month } = getMonthForISOWeek(year - 1, 53);
          if (w53Year === year && monthsInQuarter.includes(w53Month)) {
            weeksInQuarter.unshift(53);
          }
        }
      }

      if (weeksInQuarter.length > 0) {
        weeksInQuarter.sort((a, b) => a - b);
        const firstWeek = weeksInQuarter[0];
        const lastWeek = weeksInQuarter[weeksInQuarter.length - 1];

        // Calculate start date of first week
        const firstWeekYear = firstWeek > 52 ? year - 1 : year;
        const jan4First = new Date(Date.UTC(firstWeekYear, 0, 4));
        const dayOfWeekFirst = jan4First.getUTCDay() || 7;
        const startOfYearFirst = new Date(jan4First);
        startOfYearFirst.setUTCDate(jan4First.getUTCDate() - dayOfWeekFirst + 1);
        const startDate = new Date(startOfYearFirst);
        startDate.setUTCDate(startOfYearFirst.getUTCDate() + (firstWeek - 1) * 7);

        // Calculate end date of last week
        const jan4Last = new Date(Date.UTC(year, 0, 4));
        const dayOfWeekLast = jan4Last.getUTCDay() || 7;
        const startOfYearLast = new Date(jan4Last);
        startOfYearLast.setUTCDate(jan4Last.getUTCDate() - dayOfWeekLast + 1);
        const lastWeekStart = new Date(startOfYearLast);
        lastWeekStart.setUTCDate(startOfYearLast.getUTCDate() + (lastWeek - 1) * 7);
        const endDate = new Date(lastWeekStart);
        endDate.setUTCDate(lastWeekStart.getUTCDate() + 6);

        const firstWeekLabel = firstWeek > 52 ? `W${firstWeek}-${firstWeekYear}` : 'W' + String(firstWeek).padStart(2, '0');
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (ISO Weeks: ${firstWeekLabel}-W${String(lastWeek).padStart(2, '0')})`;
      }

      // Fallback to calendar quarter
      const startMonth = (quarter - 1) * 3;
      const startDate = new Date(year, startMonth, 1);
      const endDate = new Date(year, startMonth + 3, 0);
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (periodType === 'yearly') {
      const year = parseInt(period);

      // Calculate ISO week range dynamically
      const dec31 = new Date(Date.UTC(year, 11, 31));
      const weeksInYear = getISOWeekNumber(dec31);

      // Find start date (Week 1 of this year)
      const jan4 = new Date(Date.UTC(year, 0, 4));
      const dayOfWeek = jan4.getUTCDay() || 7;
      const startOfYear = new Date(jan4);
      startOfYear.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

      // Check if Week 53 of previous year belongs to this year
      const prevYearDec31 = new Date(Date.UTC(year - 1, 11, 31));
      const prevYearWeeks = getISOWeekNumber(prevYearDec31);
      let actualStartDate = new Date(startOfYear);
      let startWeekLabel = 'W01';

      if (prevYearWeeks === 53) {
        const { year: w53Year } = getMonthForISOWeek(year - 1, 53);
        if (w53Year === year) {
          // Week 53 of previous year belongs to this year
          const jan4Prev = new Date(Date.UTC(year - 1, 0, 4));
          const dayOfWeekPrev = jan4Prev.getUTCDay() || 7;
          const startOfYearPrev = new Date(jan4Prev);
          startOfYearPrev.setUTCDate(jan4Prev.getUTCDate() - dayOfWeekPrev + 1);
          actualStartDate = new Date(startOfYearPrev);
          actualStartDate.setUTCDate(startOfYearPrev.getUTCDate() + 52 * 7);
          startWeekLabel = `W53-${year - 1}`;
        }
      }

      // Find end date (last week of this year)
      const endWeekStart = new Date(startOfYear);
      endWeekStart.setUTCDate(startOfYear.getUTCDate() + (weeksInYear - 1) * 7);
      const endDate = new Date(endWeekStart);
      endDate.setUTCDate(endWeekStart.getUTCDate() + 6);

      return `${actualStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (ISO Weeks: ${startWeekLabel} through W${String(weeksInYear).padStart(2, '0')})`;
    }
    return '';
  };

  // ─── Toggle Node Expansion ─────────────────────────────────────────────────
  const toggleNode = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedNodes(newExpanded);
  };

  // ─── Calculate Row Total ───────────────────────────────────────────────────
  const calculateRowTotal = (node: HierarchyNode) => {
    const total = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
    periodsToDisplay.forEach(period => {
      const m = node.metrics[period];
      if (m) {
        total.incidentCount += m.incidentCount;
        total.totalMTTRSeconds += m.totalMTTRSeconds;
        total.lostHours += m.lostHours;
      }
    });
    return total;
  };

  // ─── Calculate Average MTTR ────────────────────────────────────────────────
  const calculateAvgMTTR = (totalSeconds: number, count: number): number => {
    if (count === 0) return 0;
    return totalSeconds / count / 60; // Convert to minutes
  };

  // ─── Get Target for Specific Period ────────────────────────────────────────
  const getTargetForPeriod = (period: string, periodType: 'weekly' | 'monthly' | 'quarterly' | 'yearly'): PeriodTarget => {
    if (periodType === 'weekly') {
      return TARGETS.weekly;
    } else if (periodType === 'monthly') {
      // Extract year and month from period string (e.g., "2025-01" -> year: 2025, month: 1)
      const match = period.match(/(\d{4})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        return getMonthlyTarget(month, year);
      }
      return TARGETS.monthly;
    } else if (periodType === 'quarterly') {
      // Extract year and quarter from period string (e.g., "2025-Q1" -> year: 2025, quarter: 1)
      const match = period.match(/(\d{4})-Q(\d)/);
      if (match) {
        const year = parseInt(match[1], 10);
        const quarter = parseInt(match[2], 10);
        return getQuarterlyTarget(quarter, year);
      }
      return TARGETS.quarterly;
    } else if (periodType === 'yearly') {
      // Extract year from period string (e.g., "2025" -> year: 2025)
      const year = parseInt(period, 10);
      return getYearlyTarget(year);
    } else {
      return TARGETS.yearly;
    }
  };

  // ─── Editable target/forecast accessor (uses editableTargets state) ────────
  // Returns effective value: per-period override if set, otherwise scaled from weekly base.
  const getScaledBase = (kpi: 'incidentCount' | 'lostHours' | 'mttr', field: 'target' | 'forecast', period: string): number => {
    const cfg = editableTargets[kpi];
    const weeklyVal = field === 'target' ? cfg.weeklyBaseTarget : cfg.weeklyBaseForecast;
    // MTTR is an average — never scale by period count
    if (kpi === 'mttr') return weeklyVal;
    // Scale count/hours: derive multiplier from the original WEEKLY_TARGET ratio
    const origWeeklyVal = kpi === 'incidentCount' ? WEEKLY_TARGET.incidentCount : WEEKLY_TARGET.lostHours;
    const origBase = getTargetForPeriod(period, periodType);
    const origPeriodVal = kpi === 'incidentCount' ? origBase.incidentCount : origBase.lostHours;
    if (origWeeklyVal === 0) return origPeriodVal;
    return parseFloat((origPeriodVal * (weeklyVal / origWeeklyVal)).toFixed(4));
  };

  const getEditableTargetForPeriod = (period: string, _pType: typeof periodType): PeriodTarget => {
    const get = (kpi: 'incidentCount' | 'lostHours' | 'mttr') => {
      const override = editableTargets[kpi].overrides[period]?.target;
      return override != null ? override : getScaledBase(kpi, 'target', period);
    };
    return { incidentCount: get('incidentCount'), lostHours: get('lostHours'), mttrAvgMinutes: get('mttr') };
  };

  const getEditableForecastForPeriod = (period: string, _pType: typeof periodType): PeriodTarget => {
    const get = (kpi: 'incidentCount' | 'lostHours' | 'mttr') => {
      const override = editableTargets[kpi].overrides[period]?.forecast;
      return override != null ? override : getScaledBase(kpi, 'forecast', period);
    };
    return { incidentCount: get('incidentCount'), lostHours: get('lostHours'), mttrAvgMinutes: get('mttr') };
  };

  // ─── Handle Cell Click ─────────────────────────────────────────────────────
  const handleCellClick = (node: HierarchyNode, period: string) => {
    console.log('📊 Dialog opened:', {
      node: node.name,
      period,
      recordCount: node.records.length,
      canEditOOP,
      sampleRecord: node.records[0] ? {
        id: node.records[0].id,
        number: node.records[0].number,
        oop: node.records[0].oop
      } : null
    });
    setSelectedCellData({ node, period });
    setDialogPage(0); // Reset pagination
    setDetailDialogOpen(true);
  };

  // ─── Helper function to get cell background color ──────────────────────────
  const getCellColor = (actual: number, target: number, metricType: 'count' | 'mttr' | 'hours'): string => {
    // No data means no incidents - that's good (green)
    if (actual === 0) return '#4caf50'; // Green

    // For all metrics: Green if at or below target, Red if over target
    if (actual <= target) {
      return '#4caf50'; // Green
    } else {
      return '#f44336'; // Red
    }
  };

  // ─── Render Hierarchy Rows ─────────────────────────────────────────────────
  const renderHierarchyRows = (nodes: HierarchyNode[], depth: number = 0, parentPath: string = ''): JSX.Element[] => {
    const rows: JSX.Element[] = [];

    nodes.forEach((node, idx) => {
      const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.has(nodePath);
      const rowTotal = calculateRowTotal(node);
      const avgMTTR = calculateAvgMTTR(rowTotal.totalMTTRSeconds, rowTotal.incidentCount);

      // Determine indent and icon
      const indent = depth * 24;
      let icon = null;
      if (hasChildren) {
        icon = isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />;
      }

      const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : '#f5f5f5';

      rows.push(
        <TableRow key={nodePath} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
          {/* Name column with hierarchy */}
          <TableCell
            sx={{
              position: 'sticky',
              left: 0,
              backgroundColor: bgColor,
              zIndex: 1,
              borderRight: '1px solid #000000',
              fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500,
              fontSize: depth === 0 ? '0.9rem' : '0.8rem',
              paddingLeft: `${8 + indent}px`,
              minWidth: 250,
              cursor: hasChildren ? 'pointer' : 'default',
              whiteSpace: 'nowrap'
            }}
            onClick={() => hasChildren && toggleNode(nodePath)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {icon}
              {!hasChildren && <Box sx={{ width: 16 }} />}
              <span>{node.name}</span>
            </Box>
          </TableCell>

          {/* Period columns - Actual values with color coding */}
          {periodsToDisplay.map(period => {
            const m = node.metrics[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
            const periodAvgMTTR = calculateAvgMTTR(m.totalMTTRSeconds, m.incidentCount);
            const target = getTargetForPeriod(period, periodType);

            return (
              <React.Fragment key={period}>
                {selectedKPIs.includes('Incident Count') && (
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: '1px solid #e0e0e0',
                      fontSize: '0.875rem',
                      minWidth: 60,
                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                      fontWeight: m.incidentCount > 0 ? 600 : 400,
                      '&:hover': m.incidentCount > 0 ? { opacity: 0.8 } : {}
                    }}
                    onClick={() => m.incidentCount > 0 && handleCellClick(node, period)}
                  >
                    {m.incidentCount || ''}
                  </TableCell>
                )}
                {selectedKPIs.includes('MTTR') && (
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: '1px solid #e0e0e0',
                      fontSize: '0.875rem',
                      minWidth: 80,
                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                      fontWeight: periodAvgMTTR > 0 ? 600 : 400,
                      '&:hover': m.incidentCount > 0 ? { opacity: 0.8 } : {}
                    }}
                    onClick={() => m.incidentCount > 0 && handleCellClick(node, period)}
                  >
                    {periodAvgMTTR > 0 ? (periodAvgMTTR / 60).toFixed(2) + 'h' : ''}
                  </TableCell>
                )}
                {selectedKPIs.includes('Lost Hours') && (
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: '1px solid #000000',
                      fontSize: '0.875rem',
                      minWidth: 65,
                      cursor: m.incidentCount > 0 ? 'pointer' : 'default',
                      fontWeight: m.lostHours > 0 ? 600 : 400,
                      '&:hover': m.incidentCount > 0 ? { opacity: 0.8 } : {}
                    }}
                    onClick={() => m.incidentCount > 0 && handleCellClick(node, period)}
                  >
                    {m.lostHours > 0 ? m.lostHours.toFixed(2) : ''}
                  </TableCell>
                )}
              </React.Fragment>
            );
          })}
        </TableRow>
      );

      if (isExpanded && hasChildren) {
        rows.push(...renderHierarchyRows(node.children!, depth + 1, nodePath));
      }
    });
    return rows;
  };

  // ─── Export CSV ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const escapeCSV = (value: string | number) => {
      const s = String(value);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // KPI definitions matching the bowler table
    type KpiRow = { metric: string; endTarget: string; uom: string; type: string; values: string[] };
    const kpiRows: KpiRow[] = [];

    const addKpi = (
      metric: string,
      uom: string,
      endTargetFn: (p: string) => string,
      targetFn: (p: string) => string,
      forecastFn: (p: string) => string,
      actualFn: (p: string) => string,
      pyFn: (p: string) => string,
    ) => {
      const firstPeriod = periodsToDisplay[0] || '';
      kpiRows.push({ metric, endTarget: firstPeriod ? endTargetFn(firstPeriod) : '-', uom, type: 'Target', values: periodsToDisplay.map(targetFn) });
      kpiRows.push({ metric, endTarget: firstPeriod ? endTargetFn(firstPeriod) : '-', uom, type: 'Forecast', values: periodsToDisplay.map(forecastFn) });
      kpiRows.push({ metric, endTarget: firstPeriod ? endTargetFn(firstPeriod) : '-', uom, type: 'Actuals', values: periodsToDisplay.map(actualFn) });
      kpiRows.push({ metric, endTarget: firstPeriod ? endTargetFn(firstPeriod) : '-', uom, type: 'PY', values: periodsToDisplay.map(pyFn) });
    };

    if (selectedKPIs.includes('Incident Count')) {
      addKpi(
        'P1/P2 Incident Count', 'Count',
        p => getEditableTargetForPeriod(p, periodType).incidentCount.toString(),
        p => getEditableTargetForPeriod(p, periodType).incidentCount.toString(),
        p => getEditableForecastForPeriod(p, periodType).incidentCount.toString(),
        p => { const pt = totals.periodTotals[p] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; return pt.incidentCount > 0 ? pt.incidentCount.toString() : '-'; },
        p => { const pk = getPrevYearPeriodKey(p); const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; return pt.incidentCount > 0 ? pt.incidentCount.toString() : '-'; },
      );
    }
    if (selectedKPIs.includes('Lost Hours')) {
      addKpi(
        'Hours Lost to Incidents', 'Hours',
        p => getEditableTargetForPeriod(p, periodType).lostHours.toFixed(1),
        p => getEditableTargetForPeriod(p, periodType).lostHours.toFixed(1),
        p => getEditableForecastForPeriod(p, periodType).lostHours.toFixed(1),
        p => { const pt = totals.periodTotals[p] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; return pt.lostHours > 0 ? pt.lostHours.toFixed(2) : '-'; },
        p => { const pk = getPrevYearPeriodKey(p); const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; return pt.lostHours > 0 ? pt.lostHours.toFixed(2) : '-'; },
      );
    }
    if (selectedKPIs.includes('MTTR')) {
      addKpi(
        'Avg MTTR', 'Hours',
        p => (getEditableTargetForPeriod(p, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
        p => (getEditableTargetForPeriod(p, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
        p => (getEditableForecastForPeriod(p, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
        p => { const pt = totals.periodTotals[p] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; const v = calculateAvgMTTR(pt.totalMTTRSeconds, pt.incidentCount); return v > 0 ? (v / 60).toFixed(2) + 'h' : '-'; },
        p => { const pk = getPrevYearPeriodKey(p); const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 }; const v = calculateAvgMTTR(pt.totalMTTRSeconds, pt.incidentCount); return v > 0 ? (v / 60).toFixed(2) + 'h' : '-'; },
      );
    }

    const headers = ['Metric', 'End Target', 'UOM', 'Type', ...periodsToDisplay.map(formatPeriod)];
    const dataRows = kpiRows.map(r => [r.metric, r.endTarget, r.uom, r.type, ...r.values]);

    const csv = [headers, ...dataRows].map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incident_ops_bowler_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 1 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0067b3' }}>
              P1/P2 Incident – Ops View
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            <IconButton size="small" onClick={() => setFiltersCollapsed(prev => !prev)} aria-label="Toggle filters">
              {filtersCollapsed ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </Box>
          <Collapse in={!filtersCollapsed}>
            <Paper elevation={2} sx={{ p: 1, mb: 3 }}>
              <Grid container spacing={1} alignItems="center">
                {/* Time Range */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={selectedTimeRange}
                      label="Time Range"
                      onChange={e => {
                        setSelectedTimeRange(e.target.value);
                        if (e.target.value === 'custom') setShowCustomDatePicker(true);
                        else setShowCustomDatePicker(false);
                      }}
                    >
                      <MenuItem value="ytd">Year to Date</MenuItem>
                      <MenuItem value="7d">Last 7 Days</MenuItem>
                      <MenuItem value="30d">Last 30 Days</MenuItem>
                      <MenuItem value="90d">Last 90 Days</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Period Type */}
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Period Type</InputLabel>
                    <Select value={periodType} label="Period Type" onChange={e => setPeriodType(e.target.value as any)}>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* KPI Selection Filter */}
                <Grid item xs={12} md={2}>
                  {makeSelectAllAutocomplete('KPI', ['Incident Count', 'MTTR', 'Lost Hours'], selectedKPIs, setSelectedKPIs)}
                </Grid>

                {showCustomDatePicker && (
                  <>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: dataDateRange.min, max: dataDateRange.max }}
                        value={customDateRange.start}
                        onChange={e => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: customDateRange.start || dataDateRange.min, max: dataDateRange.max }}
                        value={customDateRange.end}
                        onChange={e => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                      />
                    </Grid>
                  </>
                )}

                {/* OOP Toggle */}
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Button
                      size="small"
                      variant={excludeOOP ? 'contained' : 'outlined'}
                      onClick={() => setExcludeOOP(prev => !prev)}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        bgcolor: excludeOOP ? '#f44336' : undefined,
                        borderColor: '#f44336',
                        color: excludeOOP ? '#fff' : '#f44336',
                        '&:hover': { bgcolor: excludeOOP ? '#d32f2f' : '#ffeaea', borderColor: '#d32f2f' }
                      }}
                    >
                      {excludeOOP ? 'Excluding OOP' : 'Including OOP'}
                    </Button>
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} md={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" startIcon={<Clear />} onClick={clearAllFilters}>
                      Clear All
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => window.location.reload()}>
                      Refresh
                    </Button>
                    <Button variant="contained" size="small" startIcon={<FileDownload />} onClick={exportToCSV}>
                      Export CSV
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {/* Trend Cards */}
          {/* <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 1.5, borderTop: '4px solid #4caf50' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  INCIDENTS ({formatPeriod(periodComparison.currentPeriodKey)})
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {periodComparison.current.incidentCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan: {TARGETS[periodType].incidentCount}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  {periodComparison.previous.incidentCount === 0 ? (
                    <Typography variant="caption" color="text.secondary">No comparison data</Typography>
                  ) : (
                    <>
                      {periodComparison.current.incidentCount <= periodComparison.previous.incidentCount ? (
                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                          ▼ {((1 - periodComparison.current.incidentCount / periodComparison.previous.incidentCount) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                          ▲ {((periodComparison.current.incidentCount / periodComparison.previous.incidentCount - 1) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 1.5, borderTop: '4px solid #2196f3' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  AVG MTTR ({formatPeriod(periodComparison.currentPeriodKey)})
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {calculateAvgMTTR(periodComparison.current.totalMTTRSeconds, periodComparison.current.incidentCount).toFixed(0)}m
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan: {TARGETS[periodType].mttrAvgMinutes}m
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  {periodComparison.previous.incidentCount === 0 ? (
                    <Typography variant="caption" color="text.secondary">No comparison data</Typography>
                  ) : (
                    <>
                      {calculateAvgMTTR(periodComparison.current.totalMTTRSeconds, periodComparison.current.incidentCount) <= calculateAvgMTTR(periodComparison.previous.totalMTTRSeconds, periodComparison.previous.incidentCount) ? (
                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                          ▼ {((1 - calculateAvgMTTR(periodComparison.current.totalMTTRSeconds, periodComparison.current.incidentCount) / calculateAvgMTTR(periodComparison.previous.totalMTTRSeconds, periodComparison.previous.incidentCount)) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                          ▲ {((calculateAvgMTTR(periodComparison.current.totalMTTRSeconds, periodComparison.current.incidentCount) / calculateAvgMTTR(periodComparison.previous.totalMTTRSeconds, periodComparison.previous.incidentCount) - 1) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 1.5, borderTop: '4px solid #ff9800' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  LOST HOURS ({formatPeriod(periodComparison.currentPeriodKey)})
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {periodComparison.current.lostHours.toFixed(1)}h
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Plan: {TARGETS[periodType].lostHours.toFixed(1)}h
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  {periodComparison.previous.lostHours === 0 ? (
                    <Typography variant="caption" color="text.secondary">No comparison data</Typography>
                  ) : (
                    <>
                      {periodComparison.current.lostHours <= periodComparison.previous.lostHours ? (
                        <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                          ▼ {((1 - periodComparison.current.lostHours / periodComparison.previous.lostHours) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                          ▲ {((periodComparison.current.lostHours / periodComparison.previous.lostHours - 1) * 100).toFixed(0)}% vs {formatPeriod(periodComparison.previousPeriodKey)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid> */}

          {/* Bowler Chart Table */}
          <Paper elevation={2} sx={{ overflow: 'auto' }}>
            {/* Table toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: '1px solid #e0e6eb' }}>
              {canEditKPI && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => { setEditDraft(editableTargets); setEditDialogOpen(true); }}
                  sx={{ fontSize: '0.8rem' }}
                >
                  Edit Targets &amp; Forecast
                </Button>
              )}
            </Box>
            <TableContainer ref={tableContainerRef} sx={{ maxHeight: 'calc(100vh - 230px)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow ref={headerRowTopRef}>
                    {/* Fixed left columns */}
                    <TableCell align="center" sx={{ position: 'sticky', top: 0, left: 0, zIndex: 100, backgroundColor: '#42B0D5 !important', color: 'white !important', fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.3)', width: 180, minWidth: 180, maxWidth: 180, fontSize: '0.825rem', letterSpacing: '0.03em', boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>Metric</TableCell>
                    <TableCell align="center" sx={{ position: 'sticky', top: 0, left: 180, zIndex: 100, backgroundColor: '#42B0D5 !important', color: 'white !important', fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.3)', width: 80, minWidth: 80, maxWidth: 80, fontSize: '0.825rem', letterSpacing: '0.03em', boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>End Target</TableCell>
                    <TableCell align="center" sx={{ position: 'sticky', top: 0, left: 260, zIndex: 100, backgroundColor: '#42B0D5 !important', color: 'white !important', fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.3)', width: 70, minWidth: 70, maxWidth: 70, fontSize: '0.825rem', letterSpacing: '0.03em', boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>UOM</TableCell>
                    <TableCell align="center" sx={{ position: 'sticky', top: 0, left: 330, zIndex: 100, backgroundColor: '#42B0D5 !important', color: 'white !important', fontWeight: 700, borderRight: '2px solid rgba(255,255,255,0.5)', width: 120, minWidth: 120, maxWidth: 120, fontSize: '0.825rem', letterSpacing: '0.03em', boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>Type</TableCell>
                    {/* Period columns */}
                    {periodsToDisplay.map(period => (
                      <Tooltip key={period} title={getPeriodDateRange(period)} arrow placement="top">
                        <TableCell
                          data-period={period}
                          align="center"
                          sx={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#42B0D5 !important',
                            color: 'white !important',
                            fontWeight: 700,
                            borderRight: '1px solid rgba(255,255,255,0.25)',
                            fontSize: '0.825rem',
                            cursor: 'help',
                            zIndex: 90,
                            minWidth: 130,
                            letterSpacing: '0.03em'
                          }}
                        >
                          {formatPeriod(period)}
                        </TableCell>
                      </Tooltip>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4 + periodsToDisplay.length} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No data available for the selected filters</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {/* ── KPI groups ── Each selected KPI renders 3 rows: Target, Actual/Forecast, PY */}
                      {([
                        selectedKPIs.includes('Incident Count') && {
                          kpi: 'Incident Count',
                          metric: 'P1/P2 Incident Count',
                          endTarget: (period: string) => getEditableTargetForPeriod(period, periodType).incidentCount.toString(),
                          uom: 'Count',
                          getTarget: (period: string) => getEditableTargetForPeriod(period, periodType).incidentCount.toString(),
                          getForecastDisplay: (period: string) => getEditableForecastForPeriod(period, periodType).incidentCount.toString(),
                          getForecastRaw: (period: string) => getEditableForecastForPeriod(period, periodType).incidentCount,
                          getActual: (period: string) => {
                            const pt = totals.periodTotals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            const target = getEditableTargetForPeriod(period, periodType).incidentCount;
                            return { value: pt.incidentCount > 0 ? pt.incidentCount.toString() : '-', raw: pt.incidentCount, target: target, type: 'count' as const, pt };
                          },
                          getPY: (period: string) => {
                            const pk = getPrevYearPeriodKey(period);
                            const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            return { value: pt.incidentCount > 0 ? pt.incidentCount.toString() : '-', raw: pt.incidentCount, pk, pt };
                          },
                        },
                        selectedKPIs.includes('Lost Hours') && {
                          kpi: 'Lost Hours',
                          metric: 'Hours Lost to Incidents',
                          endTarget: (period: string) => getEditableTargetForPeriod(period, periodType).lostHours.toFixed(1),
                          uom: 'Hours',
                          getTarget: (period: string) => getEditableTargetForPeriod(period, periodType).lostHours.toFixed(1),
                          getForecastDisplay: (period: string) => getEditableForecastForPeriod(period, periodType).lostHours.toFixed(1),
                          getForecastRaw: (period: string) => getEditableForecastForPeriod(period, periodType).lostHours,
                          getActual: (period: string) => {
                            const pt = totals.periodTotals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            const target = getEditableTargetForPeriod(period, periodType).lostHours;
                            return { value: pt.lostHours > 0 ? pt.lostHours.toFixed(2) : '-', raw: pt.lostHours, target: target, type: 'hours' as const, pt };
                          },
                          getPY: (period: string) => {
                            const pk = getPrevYearPeriodKey(period);
                            const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            return { value: pt.lostHours > 0 ? pt.lostHours.toFixed(2) : '-', raw: pt.lostHours, pk, pt };
                          },
                        },
                        selectedKPIs.includes('MTTR') && {
                          kpi: 'MTTR',
                          metric: 'Avg MTTR',
                          endTarget: (period: string) => (getEditableTargetForPeriod(period, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
                          uom: 'Hours',
                          getTarget: (period: string) => (getEditableTargetForPeriod(period, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
                          getForecastDisplay: (period: string) => (getEditableForecastForPeriod(period, periodType).mttrAvgMinutes / 60).toFixed(2) + 'h',
                          getForecastRaw: (period: string) => getEditableForecastForPeriod(period, periodType).mttrAvgMinutes,
                          getActual: (period: string) => {
                            const pt = totals.periodTotals[period] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            const avgMTTR = calculateAvgMTTR(pt.totalMTTRSeconds, pt.incidentCount);
                            const target = getEditableTargetForPeriod(period, periodType).mttrAvgMinutes;
                            return { value: avgMTTR > 0 ? (avgMTTR / 60).toFixed(2) + 'h' : '-', raw: avgMTTR, target: target, type: 'mttr' as const, pt };
                          },
                          getPY: (period: string) => {
                            const pk = getPrevYearPeriodKey(period);
                            const pt = prevYearTotals[pk] || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0 };
                            const avgMTTR = calculateAvgMTTR(pt.totalMTTRSeconds, pt.incidentCount);
                            return { value: avgMTTR > 0 ? (avgMTTR / 60).toFixed(2) + 'h' : '-', raw: avgMTTR, pk, pt };
                          },
                        },
                      ].filter(Boolean) as Array<{
                        kpi: string;
                        metric: string;
                        endTarget: (p: string) => string;
                        uom: string;
                        getTarget: (p: string) => string;
                        getForecastDisplay: (p: string) => string;
                        getForecastRaw: (p: string) => number;
                        getActual: (p: string) => { value: string; raw: number; target: number; type: 'count' | 'hours' | 'mttr'; pt: any };
                        getPY: (p: string) => { value: string; raw: number; pk: string; pt: any };
                      }>).map((kpiDef, kpiIdx) => {
                        const firstPeriod = periodsToDisplay[0] || '';
                        const endTargetVal = firstPeriod ? kpiDef.endTarget(firstPeriod) : '-';
                        const isLast = kpiIdx === selectedKPIs.length - 1;
                        const groupBorder = '2px solid #90a4ae';

                        const stickyLeft: React.CSSProperties = { position: 'sticky' as const };
                        const cellBase = { fontSize: '0.825rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' };

                        // shared style for all 4 fixed-left cells per row
                        const leftCell = (extra?: object) => ({
                          ...cellBase,
                          backgroundColor: '#fafafa !important',
                          borderRight: '1px solid #bdbdbd',
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                          ...extra
                        });

                        return (
                          <React.Fragment key={kpiDef.kpi}>
                            {/* ── TARGET row ── */}
                            <TableRow sx={{ borderTop: groupBorder }}>
                              <TableCell rowSpan={3} align="center" sx={{ ...leftCell({ position: 'sticky', left: 0, zIndex: 10, fontWeight: 700, fontSize: '0.85rem', borderRight: '1px solid #bdbdbd', borderTop: groupBorder, verticalAlign: 'middle', width: 180, minWidth: 180, maxWidth: 180 }) }}>{kpiDef.metric}</TableCell>
                              <TableCell rowSpan={3} align="center" sx={{ ...leftCell({ position: 'sticky', left: 180, zIndex: 10, borderTop: groupBorder, verticalAlign: 'middle', width: 80, minWidth: 80, maxWidth: 80 }) }}>{endTargetVal}</TableCell>
                              <TableCell rowSpan={3} align="center" sx={{ ...leftCell({ position: 'sticky', left: 260, zIndex: 10, borderTop: groupBorder, verticalAlign: 'middle', width: 70, minWidth: 70, maxWidth: 70 }) }}>{kpiDef.uom}</TableCell>
                              <TableCell align="center" sx={{ ...leftCell({ position: 'sticky', left: 330, zIndex: 10, borderTop: groupBorder, width: 120, minWidth: 120, maxWidth: 120 }) }}>Target</TableCell>
                              {periodsToDisplay.map(period => (
                                <TableCell key={period} align="center" sx={{ ...cellBase, borderRight: '1px solid #e0e0e0', borderTop: groupBorder }}>
                                  {kpiDef.getTarget(period)}
                                </TableCell>
                              ))}
                            </TableRow>

                            {/* ── ACTUAL / FORECAST row ── */}
                            <TableRow>
                              <TableCell align="center" sx={{ ...leftCell({ position: 'sticky', left: 330, zIndex: 10, width: 120, minWidth: 120, maxWidth: 120, padding: 0 }) }}>
                                <Box sx={{ display: 'flex', width: '100%' }}>
                                  <Box sx={{ flex: 1, borderRight: '1px solid #999999', py: 0.75, fontSize: '0.825rem', fontWeight: 600 }}>Actual</Box>
                                  <Box sx={{ flex: 1, py: 0.75, fontSize: '0.825rem', fontWeight: 600 }}>Forecast</Box>
                                </Box>
                              </TableCell>
                              {periodsToDisplay.map(period => {
                                const a = kpiDef.getActual(period);
                                const f = kpiDef.getForecastDisplay(period);
                                const bgColor = a.raw === 0 ? 'inherit' : getCellColor(a.raw, a.target, a.type);
                                return (
                                  <TableCell
                                    key={period}
                                    align="center"
                                    sx={{ ...cellBase, borderRight: '1px solid #e0e0e0', padding: 0, minWidth: 130 }}
                                  >
                                    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                                      <Box
                                        sx={{
                                          flex: 1,
                                          py: 0.75,
                                          px: 0.5,
                                          borderRight: '1px solid #999999',
                                          backgroundColor: bgColor,
                                          fontWeight: 700,
                                          cursor: a.pt.incidentCount > 0 ? 'pointer' : 'default',
                                          '&:hover': a.pt.incidentCount > 0 ? { opacity: 0.8 } : {}
                                        }}
                                        onClick={() => {
                                          if (a.pt.incidentCount > 0) {
                                            const periodRecords = filteredData.filter(r => {
                                              if (!r.resolvedDate) return false;
                                              const date = parseISODate(r.resolvedDate);
                                              let pk = '';
                                              if (periodType === 'weekly') pk = `${getISOWeekYear(date)}-W${String(getISOWeekNumber(date)).padStart(2, '0')}`;
                                              else if (periodType === 'monthly') {
                                                const y = date.getUTCFullYear();
                                                const m = date.getUTCMonth() + 1;
                                                pk = `${y}-${String(m).padStart(2, '0')}`;
                                              }
                                              else if (periodType === 'quarterly') pk = getQuarterString(date);
                                              else if (periodType === 'yearly') pk = getYearString(date);
                                              return pk === period;
                                            });
                                            handleCellClick({ name: 'All Data', level: 'portfolio', metrics: {}, records: periodRecords }, period);
                                          }
                                        }}
                                      >
                                        {a.value}
                                      </Box>
                                      <Box sx={{ flex: 1, py: 0.75, px: 0.5 }}>
                                        {f}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                );
                              })}
                            </TableRow>

                            {/* ── PY row ── */}
                            <TableRow sx={{ borderBottom: isLast ? groupBorder : undefined }}>
                              <TableCell align="center" sx={{ ...leftCell({ position: 'sticky', left: 330, zIndex: 10, width: 120, minWidth: 120, maxWidth: 120 }) }}>PY</TableCell>
                              {periodsToDisplay.map(period => {
                                const py = kpiDef.getPY(period);
                                return (
                                  <TableCell
                                    key={period}
                                    align="center"
                                    sx={{
                                      ...cellBase,
                                      borderRight: '1px solid #e0e0e0',
                                      cursor: py.pt.incidentCount > 0 ? 'pointer' : 'default',
                                      '&:hover': py.pt.incidentCount > 0 ? { opacity: 0.8 } : {}
                                    }}
                                    onClick={() => {
                                      if (py.pt.incidentCount > 0) {
                                        const prevRecords = hierarchyFilteredData.filter(r => {
                                          if (!r.resolvedDate) return false;
                                          const date = parseISODate(r.resolvedDate);
                                          let pk = '';
                                          if (periodType === 'weekly') pk = `${getISOWeekYear(date)}-W${String(getISOWeekNumber(date)).padStart(2, '0')}`;
                                          else if (periodType === 'monthly') {
                                            const y = date.getUTCFullYear();
                                            const m = date.getUTCMonth() + 1;
                                            pk = `${y}-${String(m).padStart(2, '0')}`;
                                          }
                                          else if (periodType === 'quarterly') pk = getQuarterString(date);
                                          else if (periodType === 'yearly') pk = getYearString(date);
                                          return pk === py.pk;
                                        });
                                        handleCellClick({ name: 'All Data (PY)', level: 'portfolio', metrics: {}, records: prevRecords }, py.pk);
                                      }
                                    }}
                                  >
                                    {py.value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>


        </>
      )}

      {/* Incident Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedCellData?.node.name} - {selectedCellData && formatPeriod(selectedCellData.period)}
            </Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCellData && (() => {
            const filteredRecords = selectedCellData.node.records.filter(r => {
              if (!r.resolvedDate) return false;
              const date = parseISODate(r.resolvedDate);
              let periodKey: string = '';
              if (periodType === 'weekly') {
                const weekNum = getISOWeekNumber(date);
                const weekYear = getISOWeekYear(date);
                periodKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
              } else if (periodType === 'monthly') {
                // Use calendar month directly to match the period generation logic
                const y = date.getUTCFullYear();
                const m = date.getUTCMonth() + 1;
                periodKey = `${y}-${String(m).padStart(2, '0')}`;
              } else if (periodType === 'quarterly') {
                periodKey = getQuarterString(date);
              } else if (periodType === 'yearly') {
                periodKey = getYearString(date);
              }
              return periodKey === selectedCellData.period;
            });

            // Calculate totals
            const incidentCount = filteredRecords.length;
            let totalMTTRSeconds = 0;
            filteredRecords.forEach(r => {
              if (r.mttr) {
                totalMTTRSeconds += r.mttr;
              }
            });
            const avgMTTR = incidentCount > 0 ? totalMTTRSeconds / incidentCount / 60 : 0;
            const lostHours = totalMTTRSeconds / 3600;

            // Get targets for comparison
            const target = getTargetForPeriod(selectedCellData.period, periodType);

            // Previous Year Actual for dialog
            // Always source from hierarchyFilteredData (no date filter) filtered by node hierarchy,
            // because node.records only contain date-range-filtered data (e.g. YTD) and won't have PY records.
            const prevPeriodKey = getPrevYearPeriodKey(selectedCellData.period);
            const { level: nodeLevel, name: nodeName } = selectedCellData.node;
            const pyBaseRecords = hierarchyFilteredData.filter(r => {
              if (nodeName === 'All Data' || nodeName === 'All Data (PY)') return true;
              if (nodeLevel === 'portfolio') return r.portfolio === nodeName;
              if (nodeLevel === 'platform') return r.platform === nodeName;
              if (nodeLevel === 'application') return r.application === nodeName;
              return true;
            });
            const pyRecords = pyBaseRecords.filter(r => {
              if (!r.resolvedDate) return false;
              const date = parseISODate(r.resolvedDate);
              let pKey = '';
              if (periodType === 'weekly') {
                pKey = `${getISOWeekYear(date)}-W${String(getISOWeekNumber(date)).padStart(2, '0')}`;
              } else if (periodType === 'monthly') {
                // Use calendar month directly to match the period generation logic
                const y = date.getUTCFullYear();
                const m = date.getUTCMonth() + 1;
                pKey = `${y}-${String(m).padStart(2, '0')}`;
              } else if (periodType === 'quarterly') {
                pKey = getQuarterString(date);
              } else if (periodType === 'yearly') {
                pKey = getYearString(date);
              }
              return pKey === prevPeriodKey;
            });
            const pyIncidentCount = pyRecords.length;
            let pyTotalMTTRSeconds = 0;
            pyRecords.forEach(r => {
              if (r.mttr) pyTotalMTTRSeconds += r.mttr;
            });
            const pyAvgMTTR = pyIncidentCount > 0 ? pyTotalMTTRSeconds / pyIncidentCount / 60 : 0;
            const pyLostHours = pyTotalMTTRSeconds / 3600;

            return (
              <>
                {/* OOP Edit Access Notification */}
                {canEditOOP && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>OOP Edit Access:</strong> You can edit OOP (Out-of-Policy) values by clicking on the OOP column cells below.
                  </Alert>
                )}
                
                {/* Export Button */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={() => exportDialogIncidentsToCSV(filteredRecords)}
                  >
                    Export to CSV
                  </Button>
                </Box>

                {/* Incident Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Incident ID</TableCell>
                        <TableCell>Affected Portfolio</TableCell>
                        <TableCell>Affected Platform</TableCell>
                        <TableCell>Affected App</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Resolved At</TableCell>
                        <TableCell>MTTD</TableCell>
                        <TableCell>MTTR</TableCell>
                        <TableCell>OOP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords
                        .slice(dialogPage * dialogRowsPerPage, dialogPage * dialogRowsPerPage + dialogRowsPerPage)
                        .map(record => (
                          <TableRow key={record.id}>
                            <TableCell>{record.number}</TableCell>
                            <TableCell>{record.portfolio}</TableCell>
                            <TableCell>{record.platform}</TableCell>
                            <TableCell>{record.application}</TableCell>
                            <TableCell>{record.title}</TableCell>
                            <TableCell>
                              <Chip label={record.priority} color={record.priority === 'P1' ? 'error' : 'warning'} size="small" />
                            </TableCell>
                            <TableCell>{new Date(record.createdDate).toLocaleString()}</TableCell>
                            <TableCell>{record.resolvedDate ? new Date(record.resolvedDate).toLocaleString() : '-'}</TableCell>
                            <TableCell>
                              {(record.priority === 'P1' || record.priority === 'P2') && record.hasMttdData && record.mttd !== undefined ? (
                                (record.mttd / 3600).toFixed(2) + 'h'
                              ) : (record.priority === 'P1' || record.priority === 'P2') ? (
                                <Tooltip title="No data available" arrow>
                                  <span style={{
                                    color: '#9e9e9e',
                                    backgroundColor: '#f5f5f5',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    cursor: 'help'
                                  }}>
                                    N/A
                                  </span>
                                </Tooltip>
                              ) : (
                                <span style={{ color: '#9e9e9e' }}>-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.hasMttrData && record.mttr !== undefined ? (
                                (record.mttr / 3600).toFixed(2) + 'h'
                              ) : (
                                <Tooltip title="No data available" arrow>
                                  <span style={{
                                    color: '#9e9e9e',
                                    backgroundColor: '#f5f5f5',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    cursor: 'help'
                                  }}>
                                    N/A
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>
                              {canEditOOP ? (
                                editingOOPIncidentNumber === record.number ? (
                                  <Select
                                    size="small"
                                    value={oopValues.get(record.number) !== undefined ? getOOPDisplayValue(oopValues.get(record.number)) : getOOPDisplayValue(record.oop)}
                                    onChange={(e) => saveOOPValue(record.number, e.target.value)}
                                    disabled={savingOOP}
                                    autoFocus
                                    open
                                    onClose={() => setEditingOOPIncidentNumber(null)}
                                    sx={{ minWidth: 100 }}
                                  >
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                    {record.oop && record.oop.toLowerCase() !== 'manual' && record.oop.trim() !== '' && (
                                      <MenuItem value={record.oop}>{record.oop}</MenuItem>
                                    )}
                                  </Select>
                                ) : (
                                  (() => {
                                    const displayValue = getOOPDisplayValue(oopValues.get(record.number) !== undefined ? oopValues.get(record.number) : record.oop);
                                    const isEmpty = !displayValue || displayValue.trim() === '';
                                    
                                    return (
                                      <Box
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          console.log('🔵 OOP cell clicked:', record.number);
                                          setEditingOOPIncidentNumber(record.number);
                                        }}
                                        sx={{
                                          cursor: 'pointer',
                                          padding: '4px 8px',
                                          minHeight: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: isEmpty ? 'center' : 'flex-start',
                                          gap: isEmpty ? 0 : 0.5,
                                          '&:hover .edit-icon': {
                                            opacity: 1
                                          }
                                        }}
                                      >
                                        {!isEmpty && <span>{displayValue}</span>}
                                        <Edit 
                                          className="edit-icon"
                                          sx={{ 
                                            fontSize: 14, 
                                            color: '#666',
                                            opacity: 0,
                                            transition: 'opacity 0.2s'
                                          }} 
                                        />
                                      </Box>
                                    );
                                  })()
                                )
                              ) : (
                                getOOPDisplayValue(record.oop)
                              )}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredRecords.length}
                  rowsPerPage={dialogRowsPerPage}
                  page={dialogPage}
                  onPageChange={(event, newPage) => setDialogPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setDialogRowsPerPage(parseInt(event.target.value, 10));
                    setDialogPage(0);
                  }}
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Targets & Forecast Dialog ─────────────────────────────────── */}
      {editDialogOpen && (() => {
        // KPI definitions for the dialog
        const kpiDefs: Array<{ id: 'incidentCount' | 'lostHours' | 'mttr'; label: string; uom: string; step: number }> = [
          { id: 'incidentCount', label: 'P1/P2 Incident Count', uom: 'Count', step: 1 },
          { id: 'lostHours', label: 'Hours Lost to Incidents', uom: 'Hours', step: 0.1 },
          { id: 'mttr', label: 'Avg MTTR', uom: 'Minutes', step: 1 },
        ];
        const kpi = kpiDefs[editKpiTab];

        // Generate periods: from the active filter start date → end of current year
        const getEditPeriods = (pt: typeof editPeriodTab): string[] => {
          const now = new Date();
          const currentYear = now.getUTCFullYear();
          // Start from the filter start date (or Jan 1 of current year as fallback)
          const rawStart = filterStartDate
            ? new Date(filterStartDate)
            : new Date(Date.UTC(currentYear, 0, 1));
          // Always go through end of current year so no period is missed for target setting
          const rangeEnd = new Date(Date.UTC(currentYear, 11, 31));

          const periodSet = new Set<string>();
          const cur = new Date(rawStart);
          while (cur <= rangeEnd) {
            if (pt === 'weekly') {
              const w = getISOWeekNumber(cur);
              const y = getISOWeekYear(cur);
              periodSet.add(`${y}-W${String(w).padStart(2, '0')}`);
              cur.setUTCDate(cur.getUTCDate() + 7);
            } else if (pt === 'monthly') {
              periodSet.add(`${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, '0')}`);
              cur.setUTCMonth(cur.getUTCMonth() + 1);
            } else if (pt === 'quarterly') {
              const q = Math.floor(cur.getUTCMonth() / 3) + 1;
              periodSet.add(`${cur.getUTCFullYear()}-Q${q}`);
              cur.setUTCMonth(cur.getUTCMonth() + 3);
            } else {
              periodSet.add(`${cur.getUTCFullYear()}`);
              cur.setUTCFullYear(cur.getUTCFullYear() + 1);
            }
          }
          return Array.from(periodSet).sort();
        };

        const currentPeriods = getEditPeriods(editPeriodTab);

        // Helper: get draft effective value (override or scaled base)
        const getDraftScaledBase = (kpiId: 'incidentCount' | 'lostHours' | 'mttr', field: 'target' | 'forecast', period: string): number => {
          const cfg = editDraft[kpiId];
          const weeklyVal = field === 'target' ? cfg.weeklyBaseTarget : cfg.weeklyBaseForecast;
          if (kpiId === 'mttr') return weeklyVal;
          const origWeeklyVal = kpiId === 'incidentCount' ? WEEKLY_TARGET.incidentCount : WEEKLY_TARGET.lostHours;
          const origBase = getTargetForPeriod(period, editPeriodTab);
          const origPeriodVal = kpiId === 'incidentCount' ? origBase.incidentCount : origBase.lostHours;
          if (origWeeklyVal === 0) return origPeriodVal;
          return parseFloat((origPeriodVal * (weeklyVal / origWeeklyVal)).toFixed(4));
        };

        const getDraftInput = (field: 'target' | 'forecast', period: string) => {
          const ov = editDraft[kpi.id].overrides[period];
          return ov?.[field] != null ? String(ov[field]) : '';
        };

        const setDraftOverride = (field: 'target' | 'forecast', period: string, raw: string) => {
          const num = raw === '' ? null : parseFloat(raw);
          setEditDraft(prev => ({
            ...prev,
            [kpi.id]: {
              ...prev[kpi.id],
              overrides: {
                ...prev[kpi.id].overrides,
                [period]: {
                  ...prev[kpi.id].overrides[period],
                  [field]: isNaN(num as number) ? null : num,
                },
              },
            },
          }));
        };

        const resetPeriodOverride = (period: string) => {
          setEditDraft(prev => {
            const updated = { ...prev[kpi.id].overrides };
            delete updated[period];
            return { ...prev, [kpi.id]: { ...prev[kpi.id], overrides: updated } };
          });
          // Fire-and-forget DELETE to backend (draft is already updated above)
          authFetch(
            KPI_TARGETS.OVERRIDE_DELETE(LOCAL_TO_METRIC_ID[kpi.id], editPeriodTab, period, getChangedBy()),
            { method: 'DELETE' }
          ).catch(() => {/* non-blocking */ });
        };

        const hasOverride = (period: string) => {
          const ov = editDraft[kpi.id].overrides[period];
          return ov && (ov.target != null || ov.forecast != null);
        };

        return (
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Edit Targets &amp; Forecast</Typography>
                <Typography variant="caption" color="text.secondary">
                  Set a weekly base (auto-scales for other periods) or override individual periods directly.
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setEditDialogOpen(false)}><Close /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
              {/* KPI Tabs */}
              <Tabs
                value={editKpiTab}
                onChange={(_, v) => setEditKpiTab(v)}
                sx={{ borderBottom: '1px solid #e0e6eb', px: 2, backgroundColor: '#f5f7fa' }}
                TabIndicatorProps={{ style: { backgroundColor: '#42B0D5' } }}
              >
                {kpiDefs.map((k, i) => (
                  <Tab key={k.id} label={k.label} value={i} sx={{ fontSize: '0.8rem', textTransform: 'none', fontWeight: 600 }} />
                ))}
              </Tabs>

              <Box sx={{ p: 2 }}>
                {/* Weekly Base Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, backgroundColor: '#e8f4f8', borderRadius: 1, border: '1px solid #42B0D5' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 160 }}>
                    Weekly Base ({kpi.uom})
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Target:</Typography>
                    <TextField
                      size="small" type="number"
                      inputProps={{ min: 0, step: kpi.step }}
                      value={editDraft[kpi.id].weeklyBaseTarget}
                      onChange={e => setEditDraft(d => ({
                        ...d,
                        [kpi.id]: { ...d[kpi.id], weeklyBaseTarget: parseFloat(e.target.value) || 0 },
                      }))}
                      sx={{ width: 100 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>Forecast:</Typography>
                    <TextField
                      size="small" type="number"
                      inputProps={{ min: 0, step: kpi.step }}
                      value={editDraft[kpi.id].weeklyBaseForecast}
                      onChange={e => setEditDraft(d => ({
                        ...d,
                        [kpi.id]: { ...d[kpi.id], weeklyBaseForecast: parseFloat(e.target.value) || 0 },
                      }))}
                      sx={{ width: 100 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Auto-scales for monthly / quarterly / yearly unless overridden below
                  </Typography>
                </Box>

                {/* Period Type Tabs */}
                <Tabs
                  value={editPeriodTab}
                  onChange={(_, v) => setEditPeriodTab(v)}
                  sx={{ mb: 1, minHeight: 32 }}
                  TabIndicatorProps={{ style: { backgroundColor: '#42B0D5' } }}
                >
                  {(['weekly', 'monthly', 'quarterly', 'yearly'] as const).map(pt => (
                    <Tab key={pt} value={pt} label={pt.charAt(0).toUpperCase() + pt.slice(1)}
                      sx={{ fontSize: '0.75rem', minHeight: 32, textTransform: 'capitalize', py: 0.5 }} />
                  ))}
                </Tabs>

                {/* Per-Period Override Table */}
                <TableContainer sx={{ maxHeight: 340, border: '1px solid #e0e6eb', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#42B0D5 !important', color: 'white !important', minWidth: 110 }}>Period</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#42B0D5 !important', color: 'white !important', minWidth: 140 }}>
                          Target ({kpi.uom}) <br />
                          <Typography component="span" variant="caption" sx={{ fontWeight: 400, opacity: 0.85 }}>empty = use base</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#42B0D5 !important', color: 'white !important', minWidth: 140 }}>
                          Forecast ({kpi.uom}) <br />
                          <Typography component="span" variant="caption" sx={{ fontWeight: 400, opacity: 0.85 }}>empty = use base</Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#42B0D5 !important', color: 'white !important', width: 70 }}>Reset</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPeriods.map(period => {
                        const isOver = hasOverride(period);
                        const baseTgt = getDraftScaledBase(kpi.id, 'target', period);
                        const baseFct = getDraftScaledBase(kpi.id, 'forecast', period);
                        return (
                          <TableRow key={period} sx={{ backgroundColor: isOver ? '#fffde7' : 'inherit' }}>
                            <TableCell sx={{ fontWeight: isOver ? 700 : 400, fontSize: '0.8rem' }}>
                              {formatPeriod(period)}
                              {isOver && <Chip label="custom" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem', backgroundColor: '#fdd835' }} />}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number"
                                inputProps={{ min: 0, step: kpi.step }}
                                value={getDraftInput('target', period)}
                                placeholder={String(baseTgt)}
                                onChange={e => setDraftOverride('target', period, e.target.value)}
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small" type="number"
                                inputProps={{ min: 0, step: kpi.step }}
                                value={getDraftInput('forecast', period)}
                                placeholder={String(baseFct)}
                                onChange={e => setDraftOverride('forecast', period, e.target.value)}
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {isOver && (
                                <Tooltip title="Reset to base value">
                                  <IconButton size="small" onClick={() => resetPeriodOverride(period)} sx={{ color: '#f44336' }}>
                                    <Clear fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#f5f7fa', borderRadius: 1, border: '1px solid #e0e6eb' }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Tip:</strong> Leave a period blank to use the auto-scaled base value.
                    Highlighted rows (<span style={{ backgroundColor: '#fffde7', padding: '0 4px' }}>yellow</span>) have custom overrides.
                    Values are saved to the database and shared across all users.
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
              <Button variant="outlined" onClick={() => setEditDialogOpen(false)} disabled={targetsSaving}>Cancel</Button>
              <Button variant="outlined" color="error"
                onClick={async () => {
                  setEditDraft(defaultEditableTargets);
                  // Also persist defaults to backend
                  try {
                    await Promise.all(
                      (Object.keys(LOCAL_TO_METRIC_ID) as Array<keyof EditableKPIConfig>).map(key =>
                        authFetch(KPI_TARGETS.BASE_PUT, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            metric_id: LOCAL_TO_METRIC_ID[key],
                            weekly_target: defaultEditableTargets[key].weeklyBaseTarget,
                            weekly_forecast: defaultEditableTargets[key].weeklyBaseForecast,
                            changed_by: getChangedBy(),
                          }),
                        })
                      )
                    );
                  } catch {/* non-blocking */ }
                }}
                sx={{ mr: 'auto' }}
                disabled={targetsSaving}
              >
                Reset All to Defaults
              </Button>
              <Button
                variant="contained"
                disabled={targetsSaving}
                startIcon={targetsSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                onClick={async () => {
                  setTargetsSaving(true);
                  try {
                    // 1. Save base values for all KPIs
                    await Promise.all(
                      (Object.keys(LOCAL_TO_METRIC_ID) as Array<keyof EditableKPIConfig>).map(key =>
                        authFetch(KPI_TARGETS.BASE_PUT, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            metric_id: LOCAL_TO_METRIC_ID[key],
                            weekly_target: editDraft[key].weeklyBaseTarget,
                            weekly_forecast: editDraft[key].weeklyBaseForecast,
                            changed_by: getChangedBy(),
                          }),
                        })
                      )
                    );

                    // 2. Upsert all overrides in the draft
                    const overrideCalls: Promise<any>[] = [];
                    (Object.keys(LOCAL_TO_METRIC_ID) as Array<keyof EditableKPIConfig>).forEach(key => {
                      Object.entries(editDraft[key].overrides).forEach(([periodKey, ov]) => {
                        // Determine period_type from key format
                        let periodType = 'weekly';
                        if (/^\d{4}-W\d{2}$/.test(periodKey)) periodType = 'weekly';
                        else if (/^\d{4}-\d{2}$/.test(periodKey)) periodType = 'monthly';
                        else if (/^\d{4}-Q\d$/.test(periodKey)) periodType = 'quarterly';
                        else if (/^\d{4}$/.test(periodKey)) periodType = 'yearly';
                        overrideCalls.push(
                          authFetch(KPI_TARGETS.OVERRIDE, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              metric_id: LOCAL_TO_METRIC_ID[key],
                              period_type: periodType,
                              period_key: periodKey,
                              target_value: ov.target,
                              forecast_value: ov.forecast,
                              changed_by: getChangedBy(),
                            }),
                          })
                        );
                      });
                    });
                    await Promise.all(overrideCalls);

                    setEditableTargets(editDraft);
                    setEditDialogOpen(false);
                  } catch {
                    // Still update local state so UI is consistent even if API fails
                    setEditableTargets(editDraft);
                    setEditDialogOpen(false);
                  } finally {
                    setTargetsSaving(false);
                  }
                }}
              >
                {targetsSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

export default IncidentOperationsView;